import { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * useAiFeatureFlags — runtime, owner-controlled replacement for the old
 * build-time-only React env flags (REACT_APP_AI_LOOKUP / REACT_APP_DIAGNOSTIC_PROCEDURES /
 * REACT_APP_DTC_DIAGNOSIS). Those flags are unset in this deployment, so the
 * features they gated were unreachable — and even if set, they only hid the
 * UI; they never stopped a route from making a real AI call.
 *
 * Reads GET /api/ai-settings (migration 195 columns). The server is the
 * source of truth and independently enforces these toggles via
 * requireAiFeature(...) — this hook only decides what the client renders.
 *
 * All flags default to false while loading and on error, matching the
 * feature-toggle default of "off" — nothing renders until the server
 * confirms it is enabled.
 */
export interface AiFeatureFlags {
  loading: boolean;
  aiEnabled: boolean;
  aiLookupEnabled: boolean;
  diagnosticProceduresEnabled: boolean;
  dtcDiagnosisEnabled: boolean;
}

const DEFAULT_FLAGS: AiFeatureFlags = {
  loading: true,
  aiEnabled: false,
  aiLookupEnabled: false,
  diagnosticProceduresEnabled: false,
  dtcDiagnosisEnabled: false,
};

export function useAiFeatureFlags(): AiFeatureFlags {
  const [flags, setFlags] = useState<AiFeatureFlags>(DEFAULT_FLAGS);

  useEffect(() => {
    let cancelled = false;

    api
      .get('/ai-settings')
      .then((res) => {
        if (cancelled) return;
        const data = res.data ?? {};
        setFlags({
          loading: false,
          aiEnabled: !!data.ai_enabled,
          aiLookupEnabled: !!data.ai_lookup_enabled,
          diagnosticProceduresEnabled: !!data.ai_diagnostic_procedures_enabled,
          dtcDiagnosisEnabled: !!data.ai_dtc_diagnosis_enabled,
        });
      })
      .catch(() => {
        // Fail closed: on error, all AI features stay hidden (same as "off").
        if (!cancelled) setFlags({ ...DEFAULT_FLAGS, loading: false });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return flags;
}

export default useAiFeatureFlags;
